/** Expiring capacity: the inventory of seats on subscriptions already ended.
 *
 *  `prepaidUnits.warning` means the subscription has expired but Microsoft is
 *  still honouring the seats for the remainder of a grace period. That is
 *  capacity with a deadline, not capacity the tenant owns, so it is never
 *  added to paid seats anywhere on this board — it is listed here, on its own,
 *  beside what each SKU actually purchased.
 *
 *  The consequence of that gap — how many people lose a licence when each
 *  grace period closes — is the "At risk" column here and the chart in
 *  `grace-risk-panel.tsx`. This panel is the inventory; that one is the alarm. */

import { AlertTriangle, Icon } from "@norican/icons";
import { Alert, Badge, Card, DataGrid, Typography } from "@norican/react";

import type { LicenseSkuRow } from "@/lib/api";

import { formatSeats, SEAT_LABEL } from "./licence-format";

interface RenewalRiskProps {
  rows: LicenseSkuRow[];
}

export function RenewalRisk({ rows }: RenewalRiskProps) {
  if (rows.length === 0) {
    return null;
  }

  const expiringSeats = rows.reduce((sum, sku) => sum + sku.grace, 0);
  const peopleAtRisk = rows.reduce((sum, sku) => sum + sku.over_assigned, 0);

  return (
    <Card variant="outline" accent="left" accentTone="warning">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2" className="flex items-center gap-2">
            <Icon icon={AlertTriangle} size="sm" tone="warning" />
            {SEAT_LABEL.grace}
          </Typography>
          <Badge tone="warning" variant="subtle" size="sm">
            {formatSeats(expiringSeats)} seats past their subscription end
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Alert tone="warning" title="These subscriptions have already ended">
          Microsoft honours the seats until each grace period closes, then stops. They are not
          counted as paid seats anywhere on this board, because during a renewal the replacement
          subscription is already in the paid column and adding both counts it twice.
          {peopleAtRisk > 0
            ? ` ${formatSeats(peopleAtRisk)} assignments currently depend on this capacity and end with it.`
            : " No assignment currently depends on it, so it can lapse without taking a licence from anyone."}
        </Alert>
      </Card.Body>
      <Card.Body padding="none">
        <DataGrid<LicenseSkuRow>
          data={rows}
          getRowId={(sku) => sku.sku_id || sku.sku_part_number}
          density="compact"
          aria-label="SKUs with expiring capacity, with purchased and assigned seats"
          columns={[
            {
              id: "name",
              header: "SKU",
              accessor: (sku) => sku.name,
              sortable: true,
              cell: (sku) => (
                <div className="flex flex-col">
                  <span className="text-fg font-medium">{sku.name}</span>
                  <span className="text-fg-muted text-xs">{sku.sku_part_number}</span>
                </div>
              ),
            },
            {
              id: "grace",
              header: SEAT_LABEL.grace,
              numeric: true,
              sortable: true,
              accessor: (sku) => sku.grace,
              cell: (sku) => (
                <span className="text-warning-fg font-medium tabular-nums">
                  {formatSeats(sku.grace)}
                </span>
              ),
            },
            {
              id: "purchased",
              header: SEAT_LABEL.purchased,
              numeric: true,
              sortable: true,
              accessor: (sku) => sku.purchased,
              cell: (sku) => (
                <span className="text-fg tabular-nums">{formatSeats(sku.purchased)}</span>
              ),
            },
            {
              id: "consumed",
              header: SEAT_LABEL.assigned,
              numeric: true,
              sortable: true,
              accessor: (sku) => sku.consumed,
              cell: (sku) => (
                <span className="text-fg tabular-nums">{formatSeats(sku.consumed)}</span>
              ),
            },
            {
              id: "over_assigned",
              header: SEAT_LABEL.overAssigned,
              numeric: true,
              sortable: true,
              accessor: (sku) => sku.over_assigned,
              cell: (sku) =>
                sku.over_assigned > 0 ? (
                  <span className="text-danger-fg font-medium tabular-nums">
                    {formatSeats(sku.over_assigned)}
                  </span>
                ) : (
                  <span className="text-fg-subtle tabular-nums">0</span>
                ),
            },
            {
              id: "status",
              header: "Subscription",
              accessor: (sku) => sku.capability_status,
              cell: (sku) => (
                <Badge
                  tone={sku.capability_status === "Enabled" ? "neutral" : "warning"}
                  variant="subtle"
                  size="xs"
                >
                  {sku.capability_status}
                </Badge>
              ),
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
}
